import proj4 from "proj4";
import {
  Cartesian3,
  Cartographic,
  JulianDate,
  Math,
  Matrix3,
  Matrix4,
  Transforms,
  Math as CesiumMath,
  Quaternion,
} from "cesium";
import { environment } from "../env";
import { IPOI } from "../../components/matterportViewer/matterportViewer3";

export class Utils {
  public static async url2blob(url: string) {
    try {
      const data = await fetch(url);
      const blob = await data.blob();
      return blob;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  public static convertDXFToGeoJSON = (dxfData: any) => {
    const sourceProjection =
      "+proj=utm +zone=44 +datum=WGS84 +units=m +no_defs";
    const targetProjection = "+proj=longlat +datum=WGS84 +no_defs";
    const geoJSONFeatures = dxfData.entities
      .map((entity: any) => {
        if (
          entity.type === "LWPOLYLINE" ||
          entity.type === "POLYLINE" ||
          entity.type === "LINE"
        ) {
          const coordinates = entity.vertices.map((vertex: any) => {
            const [x, y] = proj4(sourceProjection, targetProjection, [
              vertex.x,
              vertex.y,
            ]);
            return [x, y];
          });
          return {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: coordinates,
            },
            properties: {},
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);
    return {
      type: "FeatureCollection",
      features: geoJSONFeatures,
    };
  };

  public static convertCesiumEntityToGeoJSON = (
    entity: any,
    properties: any
  ) => {
    const geojson: any = {
      type: "FeatureCollection",
      features: [],
    };

    if (entity.position) {
      // 📌 Convert position to longitude, latitude
      const cartographic = Cartographic.fromCartesian(
        entity.position.getValue(JulianDate.now())
      );
      const lon = Math.toDegrees(cartographic.longitude);
      const lat = Math.toDegrees(cartographic.latitude);

      geojson.features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lon, lat] },
        properties: properties,
      });
    }

    if (entity.polyline && entity.polyline.positions) {
      const positions = entity.polyline.positions.getValue(JulianDate.now());
      const coordinates = positions.map((position: Cartesian3) => {
        const cartographic = Cartographic.fromCartesian(position);
        return [
          Math.toDegrees(cartographic.longitude),
          Math.toDegrees(cartographic.latitude),
        ];
      });

      geojson.features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates },
        properties: properties,
      });
    }

    if (entity.polygon && entity.polygon.hierarchy) {
      const hierarchy = entity.polygon.hierarchy.getValue(JulianDate.now());
      const coordinates = hierarchy.positions.map((position: Cartesian3) => {
        const cartographic = Cartographic.fromCartesian(position);
        return [
          Math.toDegrees(cartographic.longitude),
          Math.toDegrees(cartographic.latitude),
        ];
      });

      coordinates.push(coordinates[0]);

      geojson.features.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [coordinates] },
        properties: properties,
      });
    }

    return geojson;
  };

  public static getElevation = async (latitude: number, longitude: number) => {
    const elevationAccessToken = environment.Elevation_Access_Token;

    var headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append(
      "Accept",
      "application/json, application/geo+json, application/gpx+xml, img/png; charset=utf-8"
    );
    var requestOpts: RequestInit = {
      method: "GET",
      headers: headers,
    };
    const getElevation = await fetch(
      `https://api.openrouteservice.org/elevation/point?api_key=${elevationAccessToken}&geometry=${longitude},${latitude}`,
      requestOpts
    );
    const result = await getElevation.json();

    const points = result.geometry?.coordinates;
    if (!points || points.length < 3) {
      return 0;
    }
    return points[2];
  };

  public static calculatePOIOrientation = (poi: IPOI) => {
    // Create a position from the POI's latitude, longitude, and altitude
    const position = Cartesian3.fromDegrees(
      poi.longitude,
      poi.latitude,
      poi.altitude
    );

    // Calculate the ENU transform matrix
    const enuTransform = Transforms.eastNorthUpToFixedFrame(position);

    // Create the rotation matrices based on the POI's heading and flip values
    const rotX = Matrix3.fromRotationX(CesiumMath.toRadians(poi.flipX || 0));
    const rotY = Matrix3.fromRotationY(CesiumMath.toRadians(poi.flipY || 0));
    const rotZ = Matrix3.fromRotationZ(CesiumMath.toRadians(poi.flipZ || 0));
    const heading = Matrix3.fromRotationZ(CesiumMath.toRadians(poi.heading));

    // Combine the rotations in the correct order
    let localRotation = Matrix3.multiply(rotX, rotY, new Matrix3());
    localRotation = Matrix3.multiply(localRotation, rotZ, localRotation);
    localRotation = Matrix3.multiply(localRotation, heading, localRotation);

    // Convert the local rotation matrix to a Matrix4
    const localRotationMatrix4 = Matrix4.fromRotationTranslation(localRotation);

    // Combine the ENU transform with the local rotation matrix
    const finalMatrix = Matrix4.multiply(
      enuTransform,
      localRotationMatrix4,
      new Matrix4()
    );

    // Create the orientation quaternion from the final rotation matrix
    const orientation = Quaternion.fromRotationMatrix(
      Matrix4.getMatrix3(finalMatrix, new Matrix3())
    );

    return orientation;
  };
}
